// React i hooks
import { memo, useCallback, useState } from "react";

// Tipovi i konstante
import { PublicApiKeysCardProps } from "./types";
import { PublicApiKey } from "services/api/settings/publicApi/types";

// UI komponente
import { Button } from "ui/components/Button/Button";
import { Dialog } from "ui/components/Dialog";
import { NoResults } from "ui/components/NoResults";
import Alert from "ui/components/Alert/Alert";
import PublicApiKeyRow from "./components/PublicApiKeyRow/PublicApiKeyRow";
import PublicApiKeyCreateCard from "./components/PublicApiKeyCreateCard/PublicApiKeyCreateCard";

// API
import { revokePublicApiKeyAPI } from "services/api/settings/publicApi/api";

interface CreateModalState {
  show: boolean;
  /** Ključ koji se rotira; prazno za obično izdavanje. */
  rotateFrom: PublicApiKey | null;
}

const CLOSED_CREATE_MODAL: CreateModalState = { show: false, rotateFrom: null };

/**
 * Popis ključeva s izdavanjem, rotacijom i opozivom. Rotacija je izdavanje novog ključa dok
 * stari još vrijedi — opoziv starog ide tek kad web stranica prijeđe na novi.
 */
const PublicApiKeysCard: React.FC<PublicApiKeysCardProps> = (props) => {
  const { settings, onChanged } = props;

  const [createModal, setCreateModal] = useState<CreateModalState>(CLOSED_CREATE_MODAL);
  const [revokeTarget, setRevokeTarget] = useState<PublicApiKey | null>(null);
  const [isRevoking, setIsRevoking] = useState<boolean>(false);

  const limitReached = settings.keys.length >= settings.maxKeys;

  const handleCreateClick = useCallback(() => {
    setCreateModal({ show: true, rotateFrom: null });
  }, []);

  const handleRotate = useCallback((apiKey: PublicApiKey) => {
    setCreateModal({ show: true, rotateFrom: apiKey });
  }, []);

  const handleCreateClose = useCallback(() => {
    setCreateModal(CLOSED_CREATE_MODAL);
  }, []);

  const handleRevokeClick = useCallback((apiKey: PublicApiKey) => {
    setRevokeTarget(apiKey);
  }, []);

  const handleRevokeCancel = useCallback(() => {
    setRevokeTarget(null);
  }, []);

  const handleRevokeConfirm = useCallback(async () => {
    if (!revokeTarget || isRevoking) return;

    setIsRevoking(true);

    try {
      await revokePublicApiKeyAPI(revokeTarget.id);
    } catch {
      setIsRevoking(false);
      return;
    }

    setIsRevoking(false);
    setRevokeTarget(null);
    onChanged();
  }, [revokeTarget, isRevoking, onChanged]);

  return (
    <>
      <div className="flex flex-col xl:flex-row gap-6 xl:gap-32 border-t border-slate-200 mt-4 pt-4">
        <div className="flex flex-col flex-1 max-w-[300px]">
          <p className="text-base font-bold text-slate-600 py-3">API ključevi</p>
          <p className="text-sm">
            Ključ se upisuje u javni API servis vaše web stranice (varijabla{" "}
            <code className="font-mono text-xs">AUTOBIZ_API_KEY</code>). Vidi se samo jednom,
            pri izdavanju. Za zamjenu bez prekida koristite rotaciju: novi ključ se izda dok
            stari još vrijedi, a stari opozovete kad stranica prijeđe na novi.
          </p>
        </div>

        <div className="flex flex-col flex-1 px-3 my-3 gap-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              {settings.keys.length} / {settings.maxKeys} ključeva
            </p>
            <Button
              variant="primary"
              size="small"
              startIcon="/media/icons/plus.svg"
              disabled={limitReached}
              onClick={handleCreateClick}
            >
              Novi ključ
            </Button>
          </div>

          {limitReached && (
            <Alert
              variant="warning"
              message={`Dosegnut je najveći broj aktivnih ključeva (${settings.maxKeys}). Opozovite ključeve koji se ne koriste.`}
            />
          )}

          <div className="rounded-sm bg-white border border-slate-200">
            {settings.keys.length === 0 ? (
              <NoResults
                title="Nema izdanih ključeva"
                message="Izdajte prvi ključ i upišite ga u javni API servis web stranice."
              />
            ) : (
              <div className="divide-y divide-slate-200">
                {settings.keys.map((apiKey) => (
                  <PublicApiKeyRow
                    key={apiKey.id}
                    apiKey={apiKey}
                    onRotate={handleRotate}
                    onRevoke={handleRevokeClick}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <PublicApiKeyCreateCard
        show={createModal.show}
        rotateFrom={createModal.rotateFrom}
        onCreated={onChanged}
        onClose={handleCreateClose}
      />

      <Dialog
        open={revokeTarget !== null}
        title="Opoziv ključa"
        message={
          revokeTarget
            ? `Ključ „${revokeTarget.name}” (${revokeTarget.keyPrefix}…) prestaje vrijediti odmah i ne može se vratiti. Web stranica koja ga koristi više neće dobivati podatke.`
            : ""
        }
        confirmText={isRevoking ? "Opozivam…" : "Opozovi"}
        cancelText="Odustani"
        onConfirm={handleRevokeConfirm}
        onCancel={handleRevokeCancel}
      />
    </>
  );
};

export default memo(PublicApiKeysCard);
